import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  getDomain, getPageSummaries, addFeaturedPage, removeFeaturedPage, reorderFeaturedPage, updateDomain,
  getDomainFavorites, setDomainFavorites, getUsers, quickCreatePage,
  getAdImpressions, getPageHits, getAdSenseEarnings, getDomainAttention, getAmazonOffers,
} from './api';
import { offersForDomain, formatCtr } from './offerMetrics';
import PublishingCalendar from './PublishingCalendar';
import DirectorySettings from './directory/DirectorySettings';
import SeoThinSettings from './seo/SeoThinSettings';
import ReaderFeedbackSettings from './feedback/ReaderFeedbackSettings';
import FeaturedStripSettings from './featured/FeaturedStripSettings';
import TimelinePanel from './timeline/TimelinePanel';
import { useToast } from './Toast';
import Spinner from './ui/Spinner';
import ConfirmDialog from './ui/ConfirmDialog';
import PageHeader from './ui/PageHeader';
import Badge from './ui/Badge';
import EmptyState from './ui/EmptyState';
import Alert from './ui/Alert';
import SiteSectionNav from './ui/SiteSectionNav';
import { previewEnabled, PREVIEW_HOST } from './PagePreviewModal';
import { openDevPreview } from './previewLinks';
import { mstDayWindow, relativeTime, sumByBaseHost, normalizeHost } from './domainMetrics';
import { listEmails } from './apiEmails';
import { senderName } from './emailHelpers';
import { formatMoney, formatCount, formatRatio } from './pageMetrics';
import { derivePageState } from './pageStatus';
import { FALLBACK_TAG, validateAmazonTag } from './earnings';
import {
  domainKpis, mergeAttentionRows, scheduledWithin, timeAgo,
} from './domainDashboard';

const SCHEDULE_DAYS = 14;
const ATTENTION_ROWS_SHOWN = 8;

const editorPath = (domainId, slug) =>
  `/domain/${encodeURIComponent(domainId)}/pages/${encodeURIComponent(slug)}/edit`;

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const emptyForm = {
  name: '', hostname: '', title: '', description: '', primary_topic: '', primary_color: '', adsense_pub_id: '',
  amazon_tag: '',
};

const formFrom = (d) => ({
  name: d.name || '',
  hostname: d.hostname || '',
  title: d.title || '',
  description: d.description || '',
  primary_topic: d.primary_topic || '',
  primary_color: d.primary_color || '',
  adsense_pub_id: d.adsense_pub_id || '',
  amazon_tag: d.amazon_tag || '',
});

/* Two views of one site, sharing the domain doc and its handlers:
     overview  /domain/:id           numbers, what needs attention, recent work
     settings  /domain/:id/settings  featured pages, categories, domain info, …
                                     (?tab= picks the section, so it can be linked) */
const SETTINGS_TABS = ['featured', 'strip', 'categories', 'info', 'directory', 'seo', 'feedback'];

const DomainDetail = ({ view = 'overview' }) => {
  const isSettings = view === 'settings';
  const { domainId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [domain, setDomain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* quick new page */
  const [quickTitle, setQuickTitle] = useState('');
  const [quickCreating, setQuickCreating] = useState(false);

  /* analytics maps (whole-fleet docs, filtered to this host client-side) */
  const [pageHits, setPageHits] = useState({});
  /* probably-human views per host; null until the refresh job labels views */
  const [pageHuman, setPageHuman] = useState(null);
  const [adsenseEarnings, setAdsenseEarnings] = useState({});
  const [adImpressions, setAdImpressions] = useState({});
  const [amazonOffers, setAmazonOffers] = useState(null);
  const [attention, setAttention] = useState(null);
  const [attentionLoading, setAttentionLoading] = useState(true);

  /* inbox preview: the newest few inbound messages for this site */
  const [recentEmails, setRecentEmails] = useState(null); // null = still loading

  /* site settings */
  const settingsTab = SETTINGS_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'featured';
  const setSettingsTab = (key) => setSearchParams((p) => {
    const q = new URLSearchParams(p);
    q.set('tab', key);
    return q;
  }, { replace: true });
  const [categoryToRemove, setCategoryToRemove] = useState(null);
  const [savingCategories, setSavingCategories] = useState(false);
  const [availablePages, setAvailablePages] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedPageId, setSelectedPageId] = useState('');
  const [selectedPosition, setSelectedPosition] = useState(0);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [featuredToRemove, setFeaturedToRemove] = useState(null);

  /* edit modal */
  const [showEditModal, setShowEditModal] = useState(false);
  const [savingDomain, setSavingDomain] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [favoritedBy, setFavoritedBy] = useState([]);
  const [editFormData, setEditFormData] = useState(emptyForm);

  const filteredAvailablePages = useMemo(() => {
    return availablePages.filter(page =>
      !domain?.featured_pages?.some(fp => fp.id === page.id)
    );
  }, [availablePages, domain?.featured_pages]);

  useEffect(() => {
    const fetchDomainData = async () => {
      try {
        setLoading(true);
        const domainData = await getDomain(domainId);
        setDomain(domainData);
        setEditFormData(formFrom(domainData));
        setFavoritedBy(domainData.favorited_by || []);
      } catch (err) {
        if (err.response && err.response.status === 401) {
          navigate('/login');
        } else {
          setError(err.response?.data?.detail || 'Failed to fetch domain details');
        }
      } finally {
        setLoading(false);
      }
    };
    if (domainId) fetchDomainData();
  }, [domainId, navigate]);

  /* The three analytics docs cover the whole fleet; they are cheap and
     independent of the domain, so fetch them once and fail apart. */
  useEffect(() => {
    if (isSettings) return undefined;
    let cancelled = false;
    getPageHits().then(d => {
      if (cancelled) return;
      setPageHits(d.hits || {});
      setPageHuman(d.human && Object.keys(d.human).length > 0 ? d.human : null);
    }).catch(() => {});
    getAdSenseEarnings().then(d => { if (!cancelled) setAdsenseEarnings(d.earnings || {}); }).catch(() => {});
    getAdImpressions().then(d => { if (!cancelled) setAdImpressions(d.domains || {}); }).catch(() => {});
    getAmazonOffers().then(d => { if (!cancelled) setAmazonOffers(d); }).catch(() => {});
    return () => { cancelled = true; };
  }, [isSettings]);

  /* Inbox preview. One small request for this domain only; the backend cannot
     order a project_id-filtered query without a composite index, so ask for a
     modest slice and sort here. Failure is silent — the inbox card just stays
     empty rather than breaking the page. */
  useEffect(() => {
    if (!domainId || isSettings) return undefined;
    let cancelled = false;
    setRecentEmails(null);
    listEmails({ domainId, perPage: 50 })
      .then((data) => {
        if (cancelled) return;
        const rows = [...(data.emails || [])].sort(
          (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
        );
        setRecentEmails(rows.slice(0, 5));
      })
      .catch(() => { if (!cancelled) setRecentEmails([]); });
    return () => { cancelled = true; };
  }, [domainId, isSettings]);

  const refreshAttention = useCallback(async () => {
    setAttentionLoading(true);
    try {
      const data = await getDomainAttention(domainId);
      setAttention(data);
    } finally {
      setAttentionLoading(false);
    }
  }, [domainId]);

  useEffect(() => {
    if (domainId && !isSettings) refreshAttention();
  }, [domainId, isSettings, refreshAttention]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(null), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  const trendDays = useMemo(() => mstDayWindow(30), []);
  const kpis = useMemo(
    () => domainKpis(domain, {
      hits: pageHits, earnings: adsenseEarnings, impressionsDomains: adImpressions, trendDays, attention,
    }),
    [domain, pageHits, adsenseEarnings, adImpressions, trendDays, attention]);

  const attentionRows = useMemo(() => mergeAttentionRows(attention), [attention]);
  const scheduledSoon = useMemo(
    () => scheduledWithin(attention && attention.scheduled, SCHEDULE_DAYS), [attention]);
  const scheduledLater = (attention && attention.scheduled ? attention.scheduled.length : 0) - scheduledSoon.length;

  const refreshDomain = useCallback(async () => {
    try {
      const updatedDomain = await getDomain(domainId);
      setDomain(updatedDomain);
    } catch (refreshErr) {
      console.error('Failed to refresh domain data:', refreshErr);
    }
  }, [domainId]);

  /* ── featured pages ─────────────────────────── */
  const handleAddFeaturedPage = useCallback(async () => {
    if (!selectedPageId) return;
    try {
      await addFeaturedPage(domainId, selectedPageId, selectedPosition);
      const selectedPage = availablePages.find(page => page.id === selectedPageId);
      if (selectedPage && domain) {
        const newFeaturedPages = [...(domain.featured_pages || [])];
        newFeaturedPages.splice(selectedPosition, 0, selectedPage);
        setDomain(prev => ({ ...prev, featured_pages: newFeaturedPages }));
      }
      setShowAddForm(false);
      setSelectedPageId('');
      setSelectedPosition(0);
      addToast('Featured page added', 'success');
    } catch (err) {
      setError('Failed to add featured page');
      refreshDomain();
    }
  }, [selectedPageId, domainId, selectedPosition, availablePages, domain, addToast, refreshDomain]);

  const handleShowAddForm = useCallback(async () => {
    if (availablePages.length === 0) {
      try {
        setPagesLoading(true);
        const pagesData = await getPageSummaries(domainId);
        setAvailablePages(pagesData);
      } catch (err) {
        setError('Failed to load pages');
        return;
      } finally {
        setPagesLoading(false);
      }
    }
    setShowAddForm(true);
  }, [domainId, availablePages.length]);

  const handleRemoveFeaturedPage = useCallback(async (pageId) => {
    try {
      await removeFeaturedPage(domainId, pageId);
      setDomain(prev => ({
        ...prev,
        featured_pages: prev.featured_pages?.filter(page => page.id !== pageId) || [],
      }));
      addToast('Removed from featured pages', 'success');
    } catch (err) {
      setError('Failed to remove featured page');
      refreshDomain();
    }
  }, [domainId, addToast, refreshDomain]);

  const handleReorderFeaturedPage = useCallback(async (pageId, direction) => {
    if (!domain?.featured_pages) return;
    const currentIndex = domain.featured_pages.findIndex(page => page.id === pageId);
    let newPosition;
    if (direction === 'up' && currentIndex > 0) newPosition = currentIndex - 1;
    else if (direction === 'down' && currentIndex < domain.featured_pages.length - 1) newPosition = currentIndex + 1;
    else return;
    try {
      await reorderFeaturedPage(domainId, pageId, newPosition);
      const newFeaturedPages = [...domain.featured_pages];
      const [movedPage] = newFeaturedPages.splice(currentIndex, 1);
      newFeaturedPages.splice(newPosition, 0, movedPage);
      setDomain(prev => ({ ...prev, featured_pages: newFeaturedPages }));
    } catch (err) {
      setError('Failed to reorder featured page');
      refreshDomain();
    }
  }, [domainId, domain?.featured_pages, refreshDomain]);

  /* ── categories (persisted via PATCH /domain, round one) ── */
  const handleReorderCategory = useCallback(async (categorySlug, direction) => {
    if (!domain?.categories) return;
    const currentIndex = domain.categories.findIndex(category => category.slug === categorySlug);
    let newPosition;
    if (direction === 'up' && currentIndex > 0) newPosition = currentIndex - 1;
    else if (direction === 'down' && currentIndex < domain.categories.length - 1) newPosition = currentIndex + 1;
    else return;
    const newCategories = [...domain.categories];
    const [movedCategory] = newCategories.splice(currentIndex, 1);
    newCategories.splice(newPosition, 0, movedCategory);
    /* Optimistic: show the new order at once, persist the whole list, and
       fall back to the server's copy if the PATCH fails. */
    setDomain(prev => ({ ...prev, categories: newCategories }));
    setSavingCategories(true);
    try {
      await updateDomain(domainId, { categories: newCategories });
    } catch (err) {
      addToast(err.response?.data?.detail || 'Failed to reorder category', 'error');
      refreshDomain();
    } finally {
      setSavingCategories(false);
    }
  }, [domainId, domain?.categories, addToast, refreshDomain]);

  const handleRemoveCategory = useCallback(async (categorySlug) => {
    const remaining = (domain?.categories || []).filter(category => category.slug !== categorySlug);
    setDomain(prev => ({ ...prev, categories: remaining }));
    setSavingCategories(true);
    try {
      await updateDomain(domainId, { categories: remaining });
      addToast('Category removed', 'success');
    } catch (err) {
      addToast(err.response?.data?.detail || 'Failed to remove category', 'error');
      refreshDomain();
    } finally {
      setSavingCategories(false);
    }
  }, [domainId, domain?.categories, addToast, refreshDomain]);

  /* ── edit domain ────────────────────────────── */
  const handleEditDomain = async () => {
    setEditFormData(formFrom(domain));
    try {
      const [favsData, usersData] = await Promise.all([getDomainFavorites(domainId), getUsers()]);
      setFavoritedBy(favsData.favorited_by || []);
      setAllUsers(usersData.filter(u => u.allowed));
    } catch (err) {
      setFavoritedBy(domain.favorited_by || []);
      console.error('Failed to load favorites/users:', err);
    }
    setShowEditModal(true);
  };

  const handleSaveDomain = async (e) => {
    if (e) e.preventDefault();
    const tagCheck = validateAmazonTag(editFormData.amazon_tag);
    if (!tagCheck.ok) {
      addToast(tagCheck.error, 'error');
      return;
    }
    setSavingDomain(true);
    try {
      await Promise.all([
        updateDomain(domainId, {
          title: editFormData.title,
          description: editFormData.description,
          primary_topic: editFormData.primary_topic,
          primary_color: editFormData.primary_color,
          adsense_pub_id: editFormData.adsense_pub_id,
          amazon_tag: tagCheck.value,
        }),
        setDomainFavorites(domainId, favoritedBy),
      ]);
      await refreshDomain();
      setShowEditModal(false);
      addToast('Domain updated', 'success');
    } catch (err) {
      addToast(err.response?.data?.detail || 'Failed to update domain', 'error');
    } finally {
      setSavingDomain(false);
    }
  };

  const handleQuickCreate = async (e) => {
    e.preventDefault();
    if (!quickTitle.trim() || quickCreating) return;
    setQuickCreating(true);
    try {
      const result = await quickCreatePage(domainId, quickTitle.trim());
      addToast(`Page "${quickTitle.trim()}" created`, 'success');
      setQuickTitle('');
      const pageId = result.page_id || result.id || result.slug;
      navigate(editorPath(domainId, pageId));
    } catch (err) {
      addToast(err.response?.data?.detail || 'Failed to create page', 'error');
    } finally {
      setQuickCreating(false);
    }
  };

  if (loading) {
    return <div className="page"><Spinner size={40} label="Loading site…" /></div>;
  }

  if (!domain) {
    return (
      <div className="page">
        <EmptyState
          title="Domain not found"
          message={error || 'This domain does not exist or you do not have access to it.'}
          action={<Link className="btn btn-secondary" to="/domains">← All sites</Link>}
        />
      </div>
    );
  }

  const host = domain.hostname || domain.name;
  const liveUrl = `https://${host}`;
  const setField = (key) => (e) => setEditFormData(prev => ({ ...prev, [key]: e.target.value }));

  // same apex/www collapse as the hits figure beside it
  const humanHits = pageHuman ? (sumByBaseHost(pageHuman)[normalizeHost(host)] || 0) : null;

  const kpiTiles = [
    { label: 'Hits 30d', value: formatCount(kpis.hits),
      sub: humanHits != null && kpis.hits > 0
        ? `${formatCount(humanHits)} probably human (${Math.round((humanHits / kpis.hits) * 100)}%)`
        : kpis.adsPerHit != null ? `${formatRatio(kpis.adsPerHit)} ads/hit` : 'complete MST days' },
    { label: 'Earnings 30d', value: formatMoney(kpis.earnings),
      sub: kpis.impressions > 0 ? `${formatCount(kpis.impressions)} impressions` : 'AdSense' },
    { label: 'eCPM', value: formatMoney(kpis.ecpm), sub: 'per 1,000 impressions' },
    { label: 'RPM', value: formatMoney(kpis.rpm), sub: 'per 1,000 page views' },
    { label: 'Pages', value: kpis.pages == null ? '—' : formatCount(kpis.pages),
      sub: kpis.newThisMonth != null ? `+${kpis.newThisMonth} this month` : 'total', to: `/domain/${domainId}/pages` },
    { label: 'Scheduled', value: kpis.scheduled == null ? '—' : formatCount(kpis.scheduled),
      sub: scheduledSoon.length > 0 ? `next: ${formatWhen(scheduledSoon[0].published_at)}` : 'nothing in the next 14 days',
      to: `/domain/${domainId}/pages` },
    { label: 'Drafts', value: kpis.drafts == null ? '—' : formatCount(kpis.drafts),
      sub: kpis.drafts > 0 ? 'waiting to be published' : 'all published', to: `/domain/${domainId}/pages` },
  ];
  /* Our own Amazon offer counts; shown for sites with a tag or any offer seen. */
  const offers = offersForDomain(amazonOffers, domainId);
  if (offers || String(domain.amazon_tag || '').trim()) {
    kpiTiles.splice(3, 0, {
      label: 'Amazon clicks 30d', value: formatCount(offers ? offers.clicks : 0),
      sub: offers ? `${formatCount(offers.impressions)} offer impressions · ${formatCtr(offers.ctr)} CTR` : 'no offers seen yet',
      to: `/domain/${domainId}/offers`,
    });
  }

  return (
    <div className="page">
      <PageHeader
        title={(
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
            {domain.favicon && (
              <img
                src={domain.favicon}
                alt=""
                width={24}
                height={24}
                style={{ borderRadius: '4px' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            {host}
          </span>
        )}
        subtitle={isSettings ? 'Site settings' : (domain.title || domain.primary_topic || 'No title set')}
        actions={isSettings ? (
          <>
            <button type="button" className="btn btn-secondary" onClick={handleEditDomain}>Edit domain</button>
            <a className="btn btn-ghost" href={liveUrl} target="_blank" rel="noopener noreferrer">Live ↗</a>
          </>
        ) : (
          <>
            <button type="button" className="btn btn-primary" onClick={() => navigate(`/domain/${domain.id}/pages/new/edit`)}>
              + New page
            </button>
            <Link className="btn btn-secondary" to={`/emails?domain=${encodeURIComponent(domain.id)}`}>Inbox</Link>
            <a className="btn btn-ghost" href={liveUrl} target="_blank" rel="noopener noreferrer">Live ↗</a>
          </>
        )}
      />
      {isSettings && <SiteSectionNav section="settings" domain={domain} />}

      <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>

      {!isSettings && (<>
      {/* KPI strip: this site only, same apex/www collapse as the Domains index */}
      <div className="kpi-grid">
        {kpiTiles.map((t) => {
          const body = (
            <>
              <div className="kpi-label">{t.label}</div>
              <div className="kpi-value">{t.value}</div>
              <div className="kpi-sub">{t.sub}</div>
            </>
          );
          return t.to
            ? <Link key={t.label} className="kpi" to={t.to} style={{ display: 'block', color: 'inherit', textDecoration: 'none' }}>{body}</Link>
            : <div key={t.label} className="kpi">{body}</div>;
        })}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '16px', alignItems: 'start', marginBottom: '28px' }}>
        {/* ── left column ── */}
        <div className="stack">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Needs attention{attentionRows.length > 0 ? ` · ${attentionRows.length}` : ''}</h3>
              <button type="button" className="btn btn-ghost btn-sm" onClick={refreshAttention} disabled={attentionLoading}>
                {attentionLoading ? 'Checking…' : 'Refresh'}
              </button>
            </div>
            {attentionLoading && !attention ? (
              <Spinner size={24} label="Checking pages…" />
            ) : attentionRows.length === 0 ? (
              <EmptyState title="Nothing needs attention" message="No drafts, missing descriptions or broken images." />
            ) : (
              <>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {attentionRows.slice(0, ATTENTION_ROWS_SHOWN).map((row) => (
                    <li key={row.slug} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 0', borderTop: '1px solid var(--color-border)' }}>
                      <Link to={editorPath(domainId, row.slug)} style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`/${row.slug}`}>
                        {row.title || `/${row.slug}`}
                      </Link>
                      <span style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                        {row.reasons.map((r) => <Badge key={r.key} state={r.state}>{r.label}</Badge>)}
                      </span>
                      <span className="muted" style={{ fontSize: '12px', flexShrink: 0, minWidth: '52px', textAlign: 'right' }}>{timeAgo(row.updated_at)}</span>
                    </li>
                  ))}
                </ul>
                {attentionRows.length > ATTENTION_ROWS_SHOWN && (
                  <div style={{ marginTop: '8px' }}>
                    <Link className="btn btn-secondary btn-sm" to={`/domain/${domainId}/pages`}>
                      All {attentionRows.length} in Pages →
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Scheduled · next {SCHEDULE_DAYS} days</h3>
              {scheduledLater > 0 && <span className="muted" style={{ fontSize: '12px' }}>+{scheduledLater} later</span>}
            </div>
            {scheduledSoon.length === 0 ? (
              <p className="muted" style={{ margin: 0, fontSize: '14px' }}>
                Nothing goes live in the next {SCHEDULE_DAYS} days.
                {' '}Set a future publish date in the editor to schedule a page.
              </p>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {scheduledSoon.map((row) => (
                  <li key={row.slug} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 0', borderTop: '1px solid var(--color-border)' }}>
                    <Link to={editorPath(domainId, row.slug)} style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`/${row.slug}`}>
                      {row.title || `/${row.slug}`}
                    </Link>
                    <Badge state="scheduled">{formatWhen(row.published_at)}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="card" id="timeline-card">
            <div className="card-header">
              <h3 className="card-title">Notes &amp; follow-ups</h3>
            </div>
            <TimelinePanel domainId={domainId} />
          </div>
        </div>

        {/* ── right column ── */}
        <div className="stack">
          <div className="card">
            <h3 className="card-title">Quick new page</h3>
            <form onSubmit={handleQuickCreate} style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                className="input"
                value={quickTitle}
                onChange={(e) => setQuickTitle(e.target.value)}
                placeholder="Page title, then Enter"
                aria-label="New page title"
                disabled={quickCreating}
                style={{ flex: 1 }}
              />
              <button type="submit" className="btn btn-primary" disabled={!quickTitle.trim() || quickCreating}>
                {quickCreating ? 'Creating…' : 'Create & edit'}
              </button>
            </form>
            <p className="muted" style={{ margin: '8px 0 0', fontSize: '12px' }}>
              Creates a draft with a slug from the title and opens the editor. <kbd>Ctrl+N</kbd> works from anywhere.
            </p>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Recently edited</h3>
              <Link className="btn btn-ghost btn-sm" to={`/domain/${domainId}/pages`}>All pages →</Link>
            </div>
            {(domain.recent_pages && domain.recent_pages.length > 0) ? (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {domain.recent_pages.map((page) => (
                  <li key={page.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 0', borderTop: '1px solid var(--color-border)' }}>
                    <Badge state={derivePageState(page)} />
                    <Link to={editorPath(domainId, page.id)} style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`/${page.slug || page.id}`}>
                      {page.title || page.slug}
                    </Link>
                    <span className="muted" style={{ fontSize: '12px', flexShrink: 0 }}>{timeAgo(page.updated_at)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted" style={{ margin: 0, fontSize: '14px' }}>No pages edited yet.</p>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Inbox</h3>
              <Link className="btn btn-ghost btn-sm" to={`/emails?domain=${encodeURIComponent(domainId)}`}>
                All messages →
              </Link>
            </div>
            {recentEmails === null ? (
              <p className="muted" style={{ margin: 0, fontSize: '14px' }}>Loading messages…</p>
            ) : recentEmails.length === 0 ? (
              <EmptyState
                title="No messages"
                message="Mail sent to this site will show up here."
              />
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {recentEmails.map((email) => (
                  <li
                    key={email.id}
                    style={{ display: 'flex', alignItems: 'baseline', gap: '8px', padding: '7px 0', borderTop: '1px solid var(--color-border)' }}
                  >
                    <Link
                      to={`/emails?domain=${encodeURIComponent(domainId)}&open=${encodeURIComponent(email.id)}`}
                      style={{ flex: 1, minWidth: 0 }}
                      title={email.subject || '(no subject)'}
                    >
                      <div style={{ fontSize: '13px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {email.subject || '(no subject)'}
                      </div>
                      <div className="muted" style={{ fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {senderName(email.sender)}
                      </div>
                    </Link>
                    <span className="muted" style={{ fontSize: '12px', flexShrink: 0 }}>{relativeTime(email.created_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <PublishingCalendar
            pageActivity={domain.page_activity || {}}
            currentStreak={domain.current_streak || 0}
            longestStreak={domain.longest_streak || 0}
          />
        </div>
      </div>
      </>)}

      {/* ── site settings ── */}
      {isSettings && (
      <section aria-label="Site settings">
        <div style={{ marginBottom: '12px' }}>
          <div className="chip-group" role="tablist" aria-label="Site settings sections">
            {[
              ['featured', `Featured pages (${domain.featured_pages ? domain.featured_pages.length : 0})`],
              ['strip', `Featured message${domain.skyscraper ? '' : ' (off)'}`],
              ['categories', `Categories (${domain.categories ? domain.categories.length : 0})`],
              ['info', 'Domain info'],
              ['directory', `Directory setup${domain.directory?.enabled ? '' : ' (off)'}`],
              ['seo', 'SEO: thin pages'],
              ['feedback', 'Reader feedback'],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={settingsTab === key}
                className={`chip${settingsTab === key ? ' active' : ''}`}
                onClick={() => setSettingsTab(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {settingsTab === 'featured' && (
          <div className="card" role="tabpanel">
            {domain.featured_pages && domain.featured_pages.length > 0 ? (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>#</th>
                      <th>Page</th>
                      <th className="actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {domain.featured_pages.map((page, index) => (
                      <tr key={page.id} className="clickable" onClick={() => navigate(editorPath(domainId, page.id))}>
                        <td className="muted">{index + 1}</td>
                        <td>
                          <strong>{page.title || page.slug}</strong>
                          <div className="muted mono" style={{ fontSize: '12px' }}>/{page.slug}</div>
                        </td>
                        <td className="actions" onClick={(e) => e.stopPropagation()}>
                          <button type="button" className="btn btn-secondary btn-sm" disabled={index === 0} title="Move up"
                                  onClick={() => handleReorderFeaturedPage(page.id, 'up')}>↑</button>
                          <button type="button" className="btn btn-secondary btn-sm" disabled={index === domain.featured_pages.length - 1} title="Move down"
                                  onClick={() => handleReorderFeaturedPage(page.id, 'down')}>↓</button>
                          <a className="btn btn-ghost btn-sm" href={`${liveUrl}/${page.slug}`} target="_blank" rel="noopener noreferrer" title="View live page">Live ↗</a>
                          <button type="button" className="btn btn-ghost btn-sm btn-danger-text" title="Remove from featured"
                                  onClick={() => setFeaturedToRemove(page)}>Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState title="No featured pages" message="Featured pages are highlighted on the site's home page." />
            )}

            <div style={{ marginTop: '12px' }}>
              {!showAddForm ? (
                <button type="button" className="btn btn-secondary" onClick={handleShowAddForm} disabled={pagesLoading}>
                  {pagesLoading ? 'Loading pages…' : '+ Add featured page'}
                </button>
              ) : (
                <div className="toolbar" style={{ marginBottom: 0 }}>
                  <select className="select" value={selectedPageId} onChange={(e) => setSelectedPageId(e.target.value)} aria-label="Page to feature" style={{ minWidth: '220px' }}>
                    <option value="">Select a page</option>
                    {filteredAvailablePages.map(page => (
                      <option key={page.id} value={page.id}>{page.title || page.slug}</option>
                    ))}
                  </select>
                  <select className="select" value={selectedPosition} onChange={(e) => setSelectedPosition(parseInt(e.target.value, 10))} aria-label="Position">
                    {Array.from({ length: (domain.featured_pages?.length || 0) + 1 }, (_, i) => (
                      <option key={i} value={i}>Position {i + 1}</option>
                    ))}
                  </select>
                  <button type="button" className="btn btn-primary" onClick={handleAddFeaturedPage} disabled={!selectedPageId}>Add</button>
                  <button type="button" className="btn btn-ghost" onClick={() => { setShowAddForm(false); setSelectedPageId(''); setSelectedPosition(0); }}>Cancel</button>
                </div>
              )}
            </div>
          </div>
        )}

        {settingsTab === 'strip' && (
          <div className="card" role="tabpanel">
            <FeaturedStripSettings
              domainId={domain.id}
              host={host}
              liveUrl={liveUrl}
              onChange={(skyscraper) => setDomain(prev => ({ ...prev, skyscraper }))}
            />
          </div>
        )}

        {settingsTab === 'categories' && (
          <div className="card" role="tabpanel">
            {domain.categories && domain.categories.length > 0 ? (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>#</th>
                      <th>Category</th>
                      <th className="actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {domain.categories.map((category, index) => (
                      <tr
                        key={category.slug || index}
                        className="clickable"
                        onClick={() => { if (category.slug) navigate(editorPath(domainId, category.slug.replace(/^\//, ''))); }}
                      >
                        <td className="muted">{index + 1}</td>
                        <td>
                          <strong>{category.title}</strong>
                          <div className="muted mono" style={{ fontSize: '12px' }}>/{category.slug}</div>
                        </td>
                        <td className="actions" onClick={(e) => e.stopPropagation()}>
                          <button type="button" className="btn btn-secondary btn-sm" disabled={index === 0 || savingCategories} title="Move up"
                                  onClick={() => handleReorderCategory(category.slug, 'up')}>↑</button>
                          <button type="button" className="btn btn-secondary btn-sm" disabled={index === domain.categories.length - 1 || savingCategories} title="Move down"
                                  onClick={() => handleReorderCategory(category.slug, 'down')}>↓</button>
                          <a className="btn btn-ghost btn-sm" href={`${liveUrl}/${category.slug}`} target="_blank" rel="noopener noreferrer" title="View live category page">Live ↗</a>
                          <button type="button" className="btn btn-ghost btn-sm btn-danger-text" disabled={savingCategories} title="Remove category"
                                  onClick={() => setCategoryToRemove(category)}>Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState
                title="No categories"
                message="Categories are stored on the domain document; add them in the backend and they will appear here."
              />
            )}
          </div>
        )}

        {settingsTab === 'directory' && (
          <div className="card" role="tabpanel">
            <div className="card-header">
              <h3 className="card-title">Directory settings</h3>
              {domain.directory?.enabled && (
                <Link className="btn btn-secondary btn-sm" to={`/domain/${domain.id}/directory`}>Open directory →</Link>
              )}
            </div>
            <DirectorySettings domainId={domain.id} onSaved={(d) => setDomain((prev) => ({ ...prev, directory: { ...(prev.directory || {}), ...d } }))} />
          </div>
        )}

        {settingsTab === 'seo' && (
          <div className="card" role="tabpanel">
            <div className="card-header">
              <h3 className="card-title">SEO: thin pages</h3>
              <Link className="btn btn-secondary btn-sm" to={`/domain/${domain.id}/pages`}>Pages →</Link>
            </div>
            <SeoThinSettings domainId={domain.id} />
          </div>
        )}

        {settingsTab === 'feedback' && (
          <div className="card" role="tabpanel">
            <div className="card-header">
              <h3 className="card-title">Reader feedback</h3>
              <Link className="btn btn-secondary btn-sm" to={`/emails?filter=feedback&domain=${encodeURIComponent(domain.id)}`}>Notes →</Link>
            </div>
            <ReaderFeedbackSettings domainId={domain.id} liveUrl={liveUrl} />
          </div>
        )}

        {settingsTab === 'info' && (
          <div className="card" role="tabpanel">
            <div className="card-header">
              <h3 className="card-title">Domain info</h3>
              <button type="button" className="btn btn-secondary btn-sm" onClick={handleEditDomain}>Edit</button>
            </div>
            <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '6px 16px', margin: 0, fontSize: '14px' }}>
              <dt className="muted">Name</dt><dd style={{ margin: 0 }}>{domain.name}</dd>
              <dt className="muted">Hostname</dt><dd style={{ margin: 0 }}><a href={liveUrl} target="_blank" rel="noopener noreferrer">{domain.hostname} ↗</a></dd>
              <dt className="muted">Title</dt><dd style={{ margin: 0 }}>{domain.title || <span className="muted">Not set</span>}</dd>
              <dt className="muted">Description</dt><dd style={{ margin: 0 }}>{domain.description || <span className="muted">Not set</span>}</dd>
              <dt className="muted">Primary topic</dt><dd style={{ margin: 0 }}>{domain.primary_topic || <span className="muted">Not set</span>}</dd>
              <dt className="muted">Primary color</dt>
              <dd style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                {domain.primary_color ? (
                  <>
                    <span style={{ display: 'inline-block', width: '16px', height: '16px', backgroundColor: domain.primary_color, border: '1px solid var(--color-border)', borderRadius: '3px' }} title={domain.primary_color} />
                    <code>{domain.primary_color}</code>
                  </>
                ) : <span className="muted">Not set (css_vars.primary_color)</span>}
              </dd>
              <dt className="muted">AdSense</dt><dd style={{ margin: 0 }}>{domain.adsense_pub_id ? <code>{domain.adsense_pub_id}</code> : <span className="muted">Not set</span>}</dd>
              <dt className="muted">Amazon tracking ID</dt>
              <dd style={{ margin: 0 }}>
                {domain.amazon_tag
                  ? <code>{domain.amazon_tag}</code>
                  : <span className="muted">Not set — Amazon links fall back to <code>{FALLBACK_TAG}</code>, so this site&apos;s Amazon earnings cannot be told apart</span>}
              </dd>
              <dt className="muted">Favorite</dt><dd style={{ margin: 0 }}>{domain.is_favorite ? 'Yes' : 'No'}</dd>
              {domain.page_stats_updated_at && (
                <><dt className="muted">Stats refreshed</dt><dd style={{ margin: 0 }}>{timeAgo(domain.page_stats_updated_at)}</dd></>
              )}
            </dl>
            <div className="toolbar" style={{ marginTop: '16px', marginBottom: 0 }}>
              {previewEnabled() && (
                <a
                  className="btn btn-secondary btn-sm"
                  href={`${PREVIEW_HOST}/about/?domain_id=${domain.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => { e.preventDefault(); openDevPreview(PREVIEW_HOST, domain.id, 'about'); }}
                >
                  View development ↗
                </a>
              )}
              <a className="btn btn-secondary btn-sm" href={liveUrl} target="_blank" rel="noopener noreferrer">View live site ↗</a>
              <a
                className="btn btn-ghost btn-sm"
                href={`https://console.cloud.google.com/firestore/databases/new-fastapi-websites-datastore/data/panel/domains/${domain.id.replace(/\./g, '_')}?project=hosting-for-digital-projects`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Firestore console ↗
              </a>
            </div>
          </div>
        )}
      </section>
      )}

      {/* ── edit modal ── */}
      {showEditModal && (
        <div className="modal-backdrop" onClick={() => !savingDomain && setShowEditModal(false)}>
          <form className="modal" role="dialog" aria-modal="true" aria-labelledby="edit-domain-title" onClick={(e) => e.stopPropagation()} onSubmit={handleSaveDomain}>
            <div className="modal-header">
              <h3 id="edit-domain-title">Edit domain</h3>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowEditModal(false)} aria-label="Close">×</button>
            </div>
            <div className="modal-body stack">
              <div className="field-row">
                <div className="field">
                  <label htmlFor="ed-name">Name</label>
                  <input id="ed-name" className="input" type="text" value={editFormData.name} readOnly />
                </div>
                <div className="field">
                  <label htmlFor="ed-host">Hostname</label>
                  <input id="ed-host" className="input" type="text" value={editFormData.hostname} readOnly />
                </div>
              </div>
              <div className="field">
                <label htmlFor="ed-title">Title</label>
                <input id="ed-title" className="input" type="text" value={editFormData.title} onChange={setField('title')} placeholder="Site title" />
              </div>
              <div className="field">
                <label htmlFor="ed-desc">Description</label>
                <textarea id="ed-desc" className="textarea" value={editFormData.description} onChange={setField('description')} placeholder="SEO description for the home page" />
              </div>
              <div className="field">
                <label htmlFor="ed-topic">Primary topic</label>
                <input id="ed-topic" className="input" type="text" value={editFormData.primary_topic} onChange={setField('primary_topic')} />
              </div>
              <div className="field">
                <label htmlFor="ed-color">Primary color</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input id="ed-color" className="input mono" type="text" value={editFormData.primary_color} onChange={setField('primary_color')} placeholder="#HEXVAL" style={{ flex: 1 }} />
                  <input type="color" value={editFormData.primary_color || '#000000'} onChange={setField('primary_color')} aria-label="Pick primary color"
                         style={{ width: '40px', height: '36px', border: '1px solid var(--color-border)', borderRadius: '6px', cursor: 'pointer', padding: 0 }} />
                </div>
              </div>
              <div className="field">
                <label htmlFor="ed-adsense">AdSense publisher ID</label>
                <input id="ed-adsense" className="input mono" type="text" value={editFormData.adsense_pub_id} onChange={setField('adsense_pub_id')} placeholder="ca-pub-1234567890123456" />
                <div className="hint">Stored as has_adsense_ads.client_id</div>
              </div>
              <div className="field">
                <label htmlFor="ed-amazon">Amazon Associates tracking ID</label>
                <input
                  id="ed-amazon"
                  className={`input mono${validateAmazonTag(editFormData.amazon_tag).ok ? '' : ' is-invalid'}`}
                  type="text"
                  value={editFormData.amazon_tag}
                  onChange={setField('amazon_tag')}
                  placeholder={`mysite-20 (blank = ${FALLBACK_TAG})`}
                  aria-invalid={!validateAmazonTag(editFormData.amazon_tag).ok}
                />
                {validateAmazonTag(editFormData.amazon_tag).ok
                  ? <div className="hint">Stored as amazon_tag; content tooling builds Amazon links with it. Leave blank to use the account fallback ({FALLBACK_TAG}).</div>
                  : <div className="error">{validateAmazonTag(editFormData.amazon_tag).error}</div>}
              </div>
              <div className="field">
                <span className="field-label">Favorited by</span>
                {allUsers.length > 0 ? (
                  <div style={{ border: '1px solid var(--color-border)', borderRadius: '6px', padding: '8px', maxHeight: '160px', overflowY: 'auto' }}>
                    {allUsers.map(user => (
                      <label key={user.uid} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '3px 0', cursor: 'pointer', fontSize: '13px', fontWeight: 400 }}>
                        <input
                          type="checkbox"
                          checked={favoritedBy.includes(user.uid)}
                          onChange={(e) => {
                            if (e.target.checked) setFavoritedBy(prev => [...prev, user.uid]);
                            else setFavoritedBy(prev => prev.filter(id => id !== user.uid));
                          }}
                        />
                        {user.name || user.email || user.uid}
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="muted" style={{ margin: 0, fontSize: '13px' }}>No users loaded.</p>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)} disabled={savingDomain}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={savingDomain}>{savingDomain ? 'Saving…' : 'Save changes'}</button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={!!categoryToRemove}
        title="Remove category?"
        message={categoryToRemove ? `"${categoryToRemove.title || categoryToRemove.slug}" will be removed from this domain's category list. The category page itself is not deleted.` : ''}
        confirmLabel="Remove"
        danger
        onConfirm={() => {
          const slug = categoryToRemove?.slug;
          setCategoryToRemove(null);
          if (slug) handleRemoveCategory(slug);
        }}
        onCancel={() => setCategoryToRemove(null)}
      />
      <ConfirmDialog
        open={!!featuredToRemove}
        title="Remove from featured pages?"
        message={featuredToRemove ? `"${featuredToRemove.title || featuredToRemove.slug}" will no longer be highlighted on the home page. The page itself is not deleted.` : ''}
        confirmLabel="Remove"
        danger
        onConfirm={() => {
          const id = featuredToRemove?.id;
          setFeaturedToRemove(null);
          if (id) handleRemoveFeaturedPage(id);
        }}
        onCancel={() => setFeaturedToRemove(null)}
      />
    </div>
  );
};

export default React.memo(DomainDetail);
